// Content board: draft/published assets (GET /contents) in an IndexTable with a
// status badge per row and an "Approve to publish" action (POST approve) with
// optimistic feedback + a toast. Clicking a row opens a detail view.
//
// Compliance: neutral capability wording only — we describe what approving does
// (queues the piece for publishing), never promise ranking/traffic/revenue.

import {
  Badge,
  BlockStack,
  Button,
  Card,
  EmptyState,
  IndexTable,
  InlineStack,
  Layout,
  Page,
  Text,
  Toast,
} from "@shopify/polaris";
import { useCallback, useEffect, useState } from "react";
import { useApi } from "../api/ApiProvider";
import { useAsync, toMessage } from "../api/hooks";
import { useStore } from "../context/StoreProvider";
import { AsyncSection } from "../components/StateViews";
import { StatusBadge } from "../components/StatusBadge";
import { ContentDetail } from "./ContentDetail";
import { formatDate, titleCase } from "../lib/format";
import type { ContentAsset } from "../api/types";

const PAGE_LIMIT = 50;

interface ToastState {
  content: string;
  error?: boolean;
}

export function ContentPage() {
  const api = useApi();
  const { store } = useStore();

  const list = useAsync(
    () => api.listContents({ store_id: store.id, limit: PAGE_LIMIT }),
    [api, store.id],
  );

  // Local mirror so we can apply optimistic markers without refetching.
  const [rows, setRows] = useState<ContentAsset[]>([]);
  useEffect(() => {
    if (list.data) setRows(list.data.items);
  }, [list.data]);

  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [approvedIds, setApprovedIds] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<ToastState | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const handleApprove = useCallback(
    async (id: string) => {
      setApprovingId(id);
      // Optimistic: mark the row approved immediately; revert on failure.
      setApprovedIds((prev) => new Set(prev).add(id));
      try {
        const result = await api.approveContent(id);
        setToast({
          content:
            result.approved > 0
              ? `Approved — ${result.approved} publish target(s) queued.`
              : "Already approved — nothing new to queue.",
        });
        list.reload();
      } catch (err) {
        setApprovedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setToast({ content: `Couldn't approve: ${toMessage(err)}`, error: true });
      } finally {
        setApprovingId(null);
      }
    },
    [api, list],
  );

  const resourceName = { singular: "content asset", plural: "content assets" };

  return (
    <Page
      title="Content"
      subtitle="Review drafts and approve them for publishing"
    >
      <Layout>
        <Layout.Section>
          <Card padding="0">
            <AsyncSection
              loading={list.loading}
              error={list.error}
              data={list.data}
              onRetry={list.reload}
              isEmpty={() => rows.length === 0}
              loadingLabel="Loading content"
              empty={
                <EmptyState
                  heading="No content yet"
                  image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
                >
                  <p>
                    Drafts generated by RankVine will appear here for you to review and approve
                    before they publish.
                  </p>
                </EmptyState>
              }
            >
              {() => (
                <IndexTable
                  resourceName={resourceName}
                  itemCount={rows.length}
                  selectable={false}
                  headings={[
                    { title: "Title" },
                    { title: "Type" },
                    { title: "Language" },
                    { title: "Status" },
                    { title: "Created" },
                    { title: "Action" },
                  ]}
                >
                  {rows.map((asset, index) => {
                    const optimisticallyApproved = approvedIds.has(asset.id);
                    return (
                      <IndexTable.Row
                        id={asset.id}
                        key={asset.id}
                        position={index}
                        onClick={() => setDetailId(asset.id)}
                      >
                        <IndexTable.Cell>
                          <Text as="span" fontWeight="semibold">
                            {asset.title || asset.slug || "(untitled)"}
                          </Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>{titleCase(asset.content_type)}</IndexTable.Cell>
                        <IndexTable.Cell>{asset.language ?? "—"}</IndexTable.Cell>
                        <IndexTable.Cell>
                          <InlineStack gap="100">
                            <StatusBadge status={asset.status} />
                            {optimisticallyApproved ? <Badge tone="info">approved</Badge> : null}
                          </InlineStack>
                        </IndexTable.Cell>
                        <IndexTable.Cell>{formatDate(asset.created_at)}</IndexTable.Cell>
                        <IndexTable.Cell>
                          {/* stopPropagation so the button doesn't also open the detail view */}
                          <div
                            onClick={(e) => e.stopPropagation()}
                            role="presentation"
                          >
                            <Button
                              size="slim"
                              variant="primary"
                              loading={approvingId === asset.id}
                              disabled={optimisticallyApproved}
                              onClick={() => handleApprove(asset.id)}
                            >
                              Approve to publish
                            </Button>
                          </div>
                        </IndexTable.Cell>
                      </IndexTable.Row>
                    );
                  })}
                </IndexTable>
              )}
            </AsyncSection>
          </Card>
        </Layout.Section>

        {list.data?.next_cursor ? (
          <Layout.Section>
            <BlockStack inlineAlign="center">
              <Text as="span" tone="subdued" variant="bodySm">
                Showing the first {rows.length}. More content assets exist.
              </Text>
            </BlockStack>
          </Layout.Section>
        ) : null}
      </Layout>

      {detailId ? (
        <ContentDetail id={detailId} onClose={() => setDetailId(null)} />
      ) : null}

      {toast ? (
        <Toast
          content={toast.content}
          error={toast.error}
          onDismiss={() => setToast(null)}
        />
      ) : null}
    </Page>
  );
}
